/**
 * agentFetch — shared fetch wrapper for every call to the Pi's API.
 *
 * Public-access build (docs/SPEC-public-access.md): the Pi requires a
 * shared passcode on every request. This wrapper:
 *   - injects `X-ReefApp-Key` from localStorage `reefapp.accessKey`
 *   - broadcasts a global "unauthorized" event when the Pi answers 401,
 *     so the AccessKeyGate overlay can prompt the user once per device
 *
 * All `lib/*-api.ts` modules route their requests through here; plain
 * `fetch` must not be used for agent calls.
 */

/** localStorage key holding this device's passcode. */
export const ACCESS_KEY_STORAGE_KEY = 'reefapp.accessKey';

export function readAccessKey(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const v = window.localStorage.getItem(ACCESS_KEY_STORAGE_KEY);
    return v && v.trim().length > 0 ? v.trim() : null;
  } catch {
    return null;
  }
}

export function writeAccessKey(value: string | null): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const trimmed = value?.trim() ?? '';
    if (trimmed.length === 0) {
      window.localStorage.removeItem(ACCESS_KEY_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ACCESS_KEY_STORAGE_KEY, trimmed);
    }
  } catch {
    // Storage unavailable — the key simply won't persist.
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Subscribe to 401 events. Returns an unsubscribe function. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized(): void {
  for (const listener of unauthorizedListeners) {
    try {
      listener();
    } catch {
      // Listener errors must never break the fetch path.
    }
  }
}

/**
 * Drop-in replacement for `fetch` on agent calls: adds the passcode
 * header and fires the unauthorized event on a 401 response.
 */
export async function agentFetch(
  input: string | URL,
  init?: RequestInit,
): Promise<Response> {
  const key = readAccessKey();
  const headers: Record<string, string> = {
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
    ...(key ? { 'x-reefapp-key': key } : {}),
  };
  const res = await fetch(input, { ...init, headers });
  if (res.status === 401) {
    notifyUnauthorized();
  }
  return res;
}
